/**
 * Duplicate detection and merge logic for activities from Strava (fitness_activities)
 * and manual entries (workout_logs).
 *
 * Matching criteria:
 *  - Same date (YYYY-MM-DD)
 *  - Same activity type (normalized)
 *  - Duration within ±5 minutes
 *  - Calories within ±20% (if both present)
 */

/** Normalize activity type strings so "Run" == "run" == "running" */
const TYPE_ALIASES = {
  run: 'running',
  ride: 'cycling',
  virtualride: 'cycling',
  walk: 'walking',
  hike: 'hiking',
  swim: 'swimming',
  weighttraining: 'strength',
  workout: 'strength',
};

export const normalizeActivityType = (type = '') => {
  const lower = type.toLowerCase().trim();
  return TYPE_ALIASES[lower] || lower;
};

/** Extract YYYY-MM-DD from any date field */
export const extractDate = (activity) => {
  const raw = activity?.date || activity?.start_time || activity?.workout_date;
  if (!raw) return null;
  return new Date(raw).toISOString().split('T')[0];
};

/** Get duration in minutes from any activity shape */
export const extractDurationMinutes = (activity) => {
  if (activity?.duration_seconds) return Math.round(activity.duration_seconds / 60);
  if (activity?.duration_minutes) return activity.duration_minutes;
  if (activity?.duration) return activity.duration;
  return null;
};

/** Get calories from any activity shape */
export const extractCalories = (activity) => {
  return activity?.calories || activity?.calories_burned || null;
};

/**
 * Returns true if two activities are considered duplicates.
 * Strava activity is `a`, manual activity is `b`.
 */
export const areDuplicates = (stravaActivity, manualActivity) => {
  // 1. Same date
  const dateA = extractDate(stravaActivity);
  const dateB = extractDate(manualActivity);
  if (!dateA || !dateB || dateA !== dateB) return false;

  // 2. Same activity type (normalized)
  const typeA = normalizeActivityType(stravaActivity?.activity_type || stravaActivity?.type || '');
  const typeB = normalizeActivityType(manualActivity?.workout_type || manualActivity?.type || '');
  if (!typeA || !typeB || typeA !== typeB) return false;

  // 3. Duration within ±5 minutes
  const durA = extractDurationMinutes(stravaActivity);
  const durB = extractDurationMinutes(manualActivity);
  if (durA !== null && durB !== null) {
    if (Math.abs(durA - durB) > 5) return false;
  }

  // 4. Calories within ±20% (only checked when both are present)
  const calA = extractCalories(stravaActivity);
  const calB = extractCalories(manualActivity);
  if (calA && calB) {
    const avg = (calA + calB) / 2;
    if (avg > 0 && Math.abs(calA - calB) / avg > 0.2) return false;
  }

  return true;
};

/**
 * Merge a Strava activity with a matching manual activity.
 * Strava is source of truth for: distance, raw_data, provider.
 * Manual entry contributes: notes, intensity, steps.
 * Returns a single merged activity object with `_merged: true`.
 */
export const mergeActivities = (stravaActivity, manualActivity) => {
  return {
    // Base: Strava data
    ...stravaActivity,
    // Enrich with manual-only fields
    intensity: manualActivity?.intensity || stravaActivity?.intensity || null,
    notes: manualActivity?.notes || stravaActivity?.notes || null,
    steps: manualActivity?.steps || stravaActivity?.steps || null,
    // Keep Strava as source of truth for these
    distance: stravaActivity?.distance ?? manualActivity?.distance ?? null,
    raw_data: stravaActivity?.raw_data || null,
    provider: stravaActivity?.provider,
    // Merge metadata
    _merged: true,
    _manualId: manualActivity?.id,
    _stravaId: stravaActivity?.id,
  };
};

/**
 * Main function: given a list of Strava activities and manual workout logs,
 * returns a deduplicated, merged array.
 *
 * - Merged entries replace both originals (shown once with merged badge).
 * - Unmatched Strava activities are included as-is.
 * - Unmatched manual activities are included as-is.
 */
export const deduplicateAndMerge = (stravaActivities = [], manualActivities = []) => {
  const usedManualIds = new Set();
  const usedStravaIds = new Set();
  const merged = [];

  for (const strava of stravaActivities) {
    let matchedManual = null;
    for (const manual of manualActivities) {
      if (usedManualIds.has(manual?.id)) continue;
      if (areDuplicates(strava, manual)) {
        matchedManual = manual;
        break;
      }
    }

    if (matchedManual) {
      usedManualIds.add(matchedManual.id);
      usedStravaIds.add(strava.id);
      merged.push(mergeActivities(strava, matchedManual));
    } else {
      merged.push(strava);
    }
  }

  // Add manual activities that were NOT matched
  for (const manual of manualActivities) {
    if (!usedManualIds.has(manual?.id)) {
      merged.push(manual);
    }
  }

  return merged;
};
